import React from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export function Privacy() {
  return (
    <div className="min-h-screen pt-20 pb-12 bg-earth-900">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="h-12 w-12 rounded-full bg-sand-400/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-sand-400" />
            </div>
            <div>
              <h1 className="text-3xl font-display">Privacy Policy</h1>
              <p className="text-sand-400">Last Updated: April 2025</p>
            </div>
          </div>

          <div className="prose prose-invert prose-sand max-w-none">
            <p className="text-sand-300">
              Rendezvous Inc. ("Rendezvous," "we," "us," or "our") respects your privacy. This Privacy Policy describes how we collect, use, disclose, and safeguard your personal information in accordance with applicable U.S. law and the General Data Protection Regulation ("GDPR") for EU/EEA users.
            </p>
            
            <p className="text-sand-300">
              This Privacy Policy applies to our websites, apps, emails, social media pages, and other digital experiences (collectively, the "Services"). By using our Services, you agree to the terms of this Privacy Policy.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">1. Personal Information We Collect</h2>
            <p className="text-sand-300">We may collect:</p>
            <ul className="list-disc list-inside text-sand-300 space-y-2">
              <li>Name, email, phone number, address</li>
              <li>Account login credentials</li>
              <li>IP address and browser/device data</li>
              <li>Payment details (via secure third parties)</li>
              <li>Location data</li>
              <li>Preferences and experience engagement history</li>
              <li>Social account identifiers (if linked)</li>
            </ul>
            <p className="text-sand-300 mt-4">
              We do not knowingly collect data from children under 16.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">2. How We Collect Information</h2>
            <ul className="list-disc list-inside text-sand-300 space-y-2">
              <li>Direct input (sign-ups, bookings)</li>
              <li>Cookies and analytics</li>
              <li>Integrated third-party services</li>
            </ul>
            <p className="text-sand-300 mt-4">
              You confirm consent when providing data about others.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">3. How We Use Your Information</h2>
            <p className="text-sand-300">We use your data to:</p>
            <ul className="list-disc list-inside text-sand-300 space-y-2">
              <li>Operate the platform and manage bookings</li>
              <li>Customize your experience and suggest content</li>
              <li>Power social graph features</li>
              <li>Share information with creators you engage with</li>
              <li>Communicate confirmations, support, and newsletters</li>
              <li>Analyze performance and prevent fraud</li>
            </ul>
            <p className="text-sand-300 mt-4">
              We do not share your data with creators you haven't engaged with.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">4. Legal Basis for EU/EEA Users</h2>
            <p className="text-sand-300">We rely on:</p>
            <ul className="list-disc list-inside text-sand-300 space-y-2">
              <li>Contractual necessity</li>
              <li>Consent</li>
              <li>Legitimate interests</li>
              <li>Legal obligations</li>
            </ul>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">5. Sharing Personal Data</h2>
            <p className="text-sand-300">We may share data:</p>
            <ul className="list-disc list-inside text-sand-300 space-y-2">
              <li>With creators for booked experiences</li>
              <li>With service providers under data protections</li>
              <li>To meet legal requirements</li>
              <li>In a business transfer</li>
            </ul>
            <p className="text-sand-300 mt-4">
              We do not sell your data.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">6. International Transfers</h2>
            <p className="text-sand-300">
              Your data may be processed in the U.S. and other countries. We apply appropriate safeguards for this.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">7. Your Rights (EU/EEA)</h2>
            <p className="text-sand-300">You may have rights to:</p>
            <ul className="list-disc list-inside text-sand-300 space-y-2">
              <li>Access, correct, delete, or transfer your data</li>
              <li>Object or restrict certain processing</li>
              <li>File complaints with regulators</li>
            </ul>
            <p className="text-sand-300 mt-4">
              Email: info@letsrendezvous.co
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">8. Cookies & Tracking</h2>
            <p className="text-sand-300">We use cookies and pixels to:</p>
            <ul className="list-disc list-inside text-sand-300 space-y-2">
              <li>Remember preferences</li>
              <li>Track usage (e.g., Google Analytics)</li>
              <li>Improve personalization</li>
            </ul>
            <p className="text-sand-300 mt-4">
              You may disable cookies in your browser.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">9. Data Security</h2>
            <p className="text-sand-300">
              We implement safeguards, though no system is fully secure. Contact us if you suspect a breach.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">10. Retention</h2>
            <p className="text-sand-300">
              We retain your information only as long as needed for service or legal purposes.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">11. Third-Party Services</h2>
            <p className="text-sand-300">
              External links are governed by third-party policies.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">12. Payments</h2>
            <p className="text-sand-300">
              Handled through services like Stripe under their terms.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">13. Updates</h2>
            <p className="text-sand-300">
              We may update this policy. Continued use implies acceptance of changes.
            </p>

            <h2 className="text-2xl font-display mt-8 mb-4 text-sand-100">14. Contact</h2>
            <p className="text-sand-300">
              If you have any questions about this Privacy Policy or how your data is handled, please contact us:
              <br />
              Email: info@letsrendezvous.co
              <br /><br />
              Legal & Registered Agent Address:
              <br />
              Corporation Service Company (Registered Agent)
              <br />
              251 Little Falls Drive
              <br />
              Wilmington, DE 19808
              <br />
              New Castle County, USA
              <br /><br />
              Please note: This is the registered agent address for legal correspondence and service of process. It is not a customer support location.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}